import { FC, ReactNode, useEffect } from "react";
import { Box } from "@mui/material";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Http } from "../../../common/http";
import { AppRoutes } from "../../../common/routesUtils";
import { ErrorResponse } from "../../../datasources/api";
import { useAuthentication } from "../../../hooks/useAuthentication";
import { makeSnackbarMessage, publishSnackbarMessage } from "../../../state/view";
import ErrorMessage, { ReloadAction } from "../../common/ErrorMessage";

interface Props {
	children: ReactNode;
	pb?: string | number;
	height?: string | number;

	errors?: Array<Error | null>;
	redirectOnForbidden?: boolean;
	redirectOnUnauthorized?: boolean;
	disableCompaniesSelector?: boolean;
	replaceContentWithError?: ReloadAction;

	onAfterDisplayError?: () => void;
}

/**
 * View is the top level component that is used to embed other views and contains all
 * elements that are shared by all views.
 *
 * Views are usually embedded in an AppLayout component (see ./AppLayout.tsx) as embedded
 * components, so that they will share the same top bar and side menu.
 *
 * @param children                 a ReactNode, usually a view
 * @param errors                   the list of errors that the view should manage;
 *                                 the view evaluates all errors and stops at the first non-null
 *                                 value, then it may show a snackbar or redirect to
 *                                 another section (see redirectOn* props)
 * @param pb                       the optional bottom padding of the view
 * @param height                   the custom height of the view, useful for some edge-case like maps
 * @param redirectOnForbidden      if true, if the evaluated error is HTTP 403 the view
 *                                 will redirect users to "Forbidden" section, otherwise
 *                                 it will present a snackbar with the error message (default: false)
 * @param redirectOnUnauthorized   if true, if the evaluated error is HTTP 401 the view
 *                                 will redirect users to "Unauthorized" section, otherwise
 *                                 it will present a snackbar with the error message (default: true)
 * @param replaceContentWithError  this option determines if errors replace the view content unless
 *                                 the first evaluated error redirects users to another section;
 *                                 in other words, if errors will not cause redirects, the
 *                                 view will replace its content with an error message
 *                                 instead of rendering a temporary snackbar (default: undefined)
 * @param onAfterDisplayError      the optional handler invoked right after publishing an error
 *
 *
 * @returns                        the child node view with additional, shared UI elements
 */
const PublicView: FC<Props> = ({
	children,
	height,
	pb,
	errors,
	redirectOnForbidden = false,
	redirectOnUnauthorized = true,
	replaceContentWithError,
	onAfterDisplayError,
}) => {
	const navigateTo = useNavigate();
	const { t } = useTranslation("errors");
	const { signOut } = useAuthentication();
	const publishMessage = useSetAtom(publishSnackbarMessage);

	const error = errors?.find((e) => e !== null) as ErrorResponse;

	// Errors management effect.
	useEffect(() => {
		if (!error) {
			return;
		}

		// Check if the error cause is "Unauthorized" (HTTP 401).
		if (error.httpCode === Http.Unauthorized && redirectOnUnauthorized) {
			signOut();
			navigateTo(AppRoutes.Unauthorized);
			return;
		}

		// Check if the error cause is "Forbidden" (HTTP 403).
		if (error.httpCode === Http.Forbidden && redirectOnForbidden) {
			navigateTo(AppRoutes.Forbidden);
			return;
		}

		// Check if the error should be presented as a temporary message
		// or if it should replace the view content.
		if (!replaceContentWithError) {
			// Otherwise publish an error message.
			const message = t(error.message);
			publishMessage(makeSnackbarMessage(message, "error"));
			onAfterDisplayError?.();
		}
	}, [
		error,
		publishMessage,
		navigateTo,
		signOut,
		onAfterDisplayError,
		t,
		redirectOnForbidden,
		redirectOnUnauthorized,
		replaceContentWithError,
	]);

	return (
		<Box component="main" sx={{ flexGrow: 1, p: 3, pt: 10, height, pb }}>
			{error && replaceContentWithError && (
				<ErrorMessage httpStatusCode={error.httpCode} accept={replaceContentWithError} />
			)}

			{(!replaceContentWithError || !error) && children}
		</Box>
	);
};

export { PublicView };
